import * as React from 'react';
import { css } from 'aphrodite';
import { IAutomationStepStatus } from '../types';
import { styleSheet } from '../styles';

interface ContactProgressIndicatorProps {
	steps: IAutomationStepStatus[];
}

export const ContactProgressIndicator: React.FC<ContactProgressIndicatorProps> = ({ steps }) => {
	const getStatusStyle = (status: string) => {
		const lowerStatus = status.toLowerCase();
		if (lowerStatus === 'queued') {
			return styleSheet.statusQueued;
		}
		if (lowerStatus === 'sent') {
			return styleSheet.statusSent;
		}
		if (lowerStatus === 'opened') {
			return styleSheet.statusOpened;
		}
		if (lowerStatus === 'failed' || lowerStatus === 'bounced') {
			return styleSheet.statusFailed;
		}
		return styleSheet.statusQueued;
	};

	const formatDueDate = (dateString: string) => {
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			});
		} catch {
			return dateString;
		}
	};

	const getStepName = (step: IAutomationStepStatus['step']) => {
		const stepType = step._type;
		if (stepType === 'EmailAutomationStep') {
			return `Email: ${step.name || 'Untitled'}`;
		}
		if (stepType === 'HtmlNewsletterAutomationStep') {
			return `Newsletter: ${step.name || 'Untitled'}`;
		}
		if (stepType === 'TextingAutomationStep') {
			return `Texting: ${step.name || 'Untitled'}`;
		}
		if (stepType === 'ActionItemAutomationStep') {
			return `Action Item: ${step.name || 'Untitled'}`;
		}
		if (stepType === 'SwitchAutomationStep') {
			return `Switch: ${step.name || 'Untitled'}`;
		}
		if (stepType === 'AddTagAutomationStep') {
			const tagStep = step as { tagName?: string; name?: string };
			return `Add Tag: ${tagStep.tagName || 'Untitled'}`;
		}
		if (stepType === 'RemoveTagAutomationStep') {
			const tagStep = step as { tagName?: string; name?: string };
			return `Remove Tag: ${tagStep.tagName || 'Untitled'}`;
		}
		if (stepType === 'HandwrittenCardAutomationStep') {
			return `Card: ${step.name || 'Untitled'}`;
		}
		if (stepType === 'OpportunityAutomationStep') {
			return `Opportunity: ${step.name || 'Untitled'}`;
		}
		if (stepType === 'NoActionAutomationStep') {
			return `No Action`;
		}
		// Fallback for any unknown step types
		const baseType = stepType?.replace('AutomationStep', '') || 'Step';
		return step.name ? `${baseType}: ${step.name}` : baseType;
	};

	if (!steps || steps.length === 0) {
		return null;
	}

	return (
		<ol className={css(styleSheet.progressIndicator)} aria-label='Automation steps progress'>
			{steps.map((stepStatus, index) => (
				<li key={stepStatus.id || index} className={css(styleSheet.progressStep)}>
					<span className={css(styleSheet.progressStepStatus, getStatusStyle(stepStatus.status.status))}>
						{stepStatus.status.status}
					</span>
					<span className={css(styleSheet.progressStepName)}>{getStepName(stepStatus.step)}</span>
					{!stepStatus.isFinished && stepStatus.dueDate && (
						<time className={css(styleSheet.progressStepDueDate)} dateTime={stepStatus.dueDate}>
							{formatDueDate(stepStatus.dueDate)}
						</time>
					)}
				</li>
			))}
		</ol>
	);
};
